// status-progress-journey-derivation.ts
//
// Progress panel P1: `progress.journey[]` — the mission's fixed step order
// g0 → g1 → survey → plan → g2 → delivering → review → integration → g3 →
// release (`review` = the controller-reserved review candidates, beta.2),
// derived ONLY from sources that already exist (gates.json, ticket groups,
// the status label, the effective release config). No "phase" function
// existed (SU F10); this one composes, it does not re-decide anything the
// controller decides. Pure. Rules: phase-01 §Architecture tables.
import { isControllerReservedTicketId } from "./controller-reserved-ticket-ids.js";
import {
  deliveringSummary,
  GATE_IDS,
  gateStep,
  groupStep,
  integrationStep,
  type JourneyInput,
  type JourneyTicket,
  liveRecoveries,
  PROGRESSED,
  releaseStep,
  reviewStep,
} from "./status-progress-journey-steps.js";
import {
  classifyStatusLabel,
  type StatusLabelEffect,
} from "./status-progress-status-label-classes.js";
import type { JourneyStep, JourneyStepId } from "./status-progress-types.js";

export {
  CLOSED_TICKET_STATUSES,
  type JourneyInput,
  type JourneyRecovery,
  type JourneyTicket,
} from "./status-progress-journey-steps.js";

/** Y1: the one terminal rule — ABORTED (sticky) ∨ released ∨ done_without_deploy ∨ done without release. */
export function isMissionTerminalFromStatusLabel(
  label: string | undefined,
  releaseConfigured: boolean,
): boolean {
  if (label === "ABORTED" || label === "released" || label === "done_without_deploy") return true;
  return label === "done" && !releaseConfigured;
}

export function activeStageOf(journey: readonly JourneyStep[]): JourneyStepId | undefined {
  return journey.find((s) => s.state !== "done" && s.state !== "skipped")?.id;
}

/** Overlay of a non-stale label on `active_stage` (bảng nhãn). Mutates the fresh array. */
function applyLabelOverlay(journey: JourneyStep[], effect: StatusLabelEffect, label: string) {
  const source = `label:${label}`;
  if (effect.kind === "aborted") {
    for (const step of journey)
      if (step.state !== "done") Object.assign(step, { state: "skipped", source });
    return;
  }
  if (effect.kind === "plan_not_ready") {
    const plan = journey.find((s) => s.id === "plan");
    if (plan) Object.assign(plan, { state: "blocked", summary: "plan chưa đủ cho G2", source });
    return;
  }
  const at = journey.findIndex((s) => s.state !== "done" && s.state !== "skipped");
  const active = journey[at];
  if (active === undefined) return;
  if (effect.kind === "keep" && effect.summary !== undefined) active.summary = effect.summary;
  if (effect.kind === "interview" || effect.kind === "cost_ceiling") {
    Object.assign(active, { state: "waiting_human", source });
  }
  if (effect.kind === "active_blocked")
    Object.assign(active, { state: "blocked", summary: effect.code, source });
  if (effect.kind === "unrecognized") Object.assign(active, { state: "unknown", source });
  if (effect.kind === "gate_unknown") {
    const gate = GATE_IDS.has(active.id)
      ? active
      : journey
          .slice(0, at)
          .reverse()
          .find((s) => GATE_IDS.has(s.id));
    if (gate) Object.assign(gate, { state: "unknown", source });
  }
}

export function deriveJourney(input: JourneyInput): JourneyStep[] {
  const effect = input.labelStale
    ? ({ kind: "none" } as const)
    : classifyStatusLabel(input.statusLabel);
  const planRecoveries = new Set(
    input.recoveries.filter((r) => r.trigger === "plan_blocked").map((r) => r.recovery_ticket),
  );
  const live = liveRecoveries(input);
  const recovering = new Set(live.map((r) => r.source_ticket));
  const isRelease = (x: JourneyTicket) => x.kind === "deploy" || x.kind === "revert";
  const groups: Record<"survey" | "plan" | "delivering" | "review", JourneyTicket[]> = {
    survey: input.tickets.filter((x) => x.kind === "survey"),
    plan: input.tickets.filter((x) => x.kind === "plan" || planRecoveries.has(x.id)),
    delivering: input.tickets.filter(
      (x) =>
        x.kind !== "survey" &&
        x.kind !== "plan" &&
        !isRelease(x) &&
        !planRecoveries.has(x.id) &&
        !isControllerReservedTicketId(x.id),
    ),
    // DF-P4-1: its own step, so a running review is never hidden behind
    // "no release config ⇒ integration skipped".
    review: input.tickets.filter((x) => isControllerReservedTicketId(x.id)),
  };
  const plan = groupStep("plan", groups.plan, recovering);
  if (live.some((r) => r.trigger === "plan_blocked")) {
    Object.assign(plan, { state: "active", summary: "đang gỡ kẹt plan" });
  }
  const delivering = groupStep("delivering", groups.delivering, recovering);
  const summary = deliveringSummary(groups.delivering);
  if (summary !== undefined) delivering.summary = summary;
  const missionTerminal = isMissionTerminalFromStatusLabel(
    input.statusLabel,
    input.releaseConfigured,
  );
  const g3 = gateStep(input.gates, "g3");
  const noRelease = !input.releaseConfigured && g3.state === "pending";
  const journey: JourneyStep[] = [
    gateStep(input.gates, "g0"),
    gateStep(input.gates, "g1"),
    groupStep("survey", groups.survey, recovering),
    plan,
    gateStep(input.gates, "g2"),
    delivering,
    reviewStep(groups.review, recovering, missionTerminal),
    integrationStep(input, effect),
    noRelease ? { ...g3, state: "skipped", source: "config:release" } : g3,
    releaseStep(input, effect, input.tickets.filter(isRelease)),
  ];
  const empty = new Set(
    Object.entries(groups)
      .filter(([, g]) => g.length === 0)
      .map(([id]) => id),
  );
  skipEmptyPassedSteps(journey, empty);
  // No review candidate ever ran and the mission is over (e.g. a pre-review CLI's journal).
  const review = journey.find((s) => s.id === "review");
  if (review?.state === "pending" && empty.has("review") && missionTerminal) {
    review.state = "skipped";
  }
  if (input.statusLabel !== undefined) applyLabelOverlay(journey, effect, input.statusLabel);
  return journey;
}

/** An empty ticket group is `skipped` only once a LATER step moved; before that it is still to come. */
function skipEmptyPassedSteps(journey: JourneyStep[], empty: ReadonlySet<string>) {
  journey.forEach((step, i) => {
    if (!empty.has(step.id) || step.state !== "pending") return;
    if (journey.slice(i + 1).some((later) => PROGRESSED.has(later.state))) step.state = "skipped";
  });
}
